import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getBrands } from '@/lib/queries/brands'
import { useBrandStore } from '@/store/brandStore'

/**
 * Brand picker: selects an active brandId and navigates back to `returnTo`
 * (if it contains a `/brands/:brandId/...` route), otherwise defaults to Settings.
 */
export default function BrandsPicker() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const selectedBrandId = searchParams.get('brandId') || undefined
  const setSelectedBrandId = useBrandStore((s) => s.setSelectedBrandId)
  const storedBrandId = useBrandStore((s) => s.selectedBrandId)

  const { data: brands, isLoading } = useQuery({
    queryKey: ['brands'],
    queryFn: getBrands,
    staleTime: 0,
  })

  const handlePick = (brandId: string) => {
    setSelectedBrandId(brandId)
    if (returnTo) {
      // Replace the brand segment only when the returnTo path matches the `/brands/<id>/...` pattern.
      const replaced = returnTo.replace(/\/brands\/[^/]+/, `/brands/${brandId}`)
      if (replaced !== returnTo) {
        navigate(replaced)
        return
      }
    }

    navigate(`/brands/${brandId}/settings`)
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <h1 className="text-xl font-bold text-gray-900">Choose Brand</h1>
          <div className="text-xs text-gray-600 mt-1">Select the brand you want to work on.</div>
        </div>
      </header>

      <main className="p-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          {isLoading ? (
            <div className="text-sm text-gray-600">Loading brands…</div>
          ) : (
            <div className="space-y-3">
              {(brands || []).map((brand) => (
                <button
                  key={brand._id}
                  type="button"
                  onClick={() => handlePick(brand._id)}
                  className={`w-full text-left border rounded-lg p-3 transition-colors touch-target-min-44 ${
                    brand._id === selectedBrandId
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:bg-blue-50'
                  }`}
                  aria-label={`Select ${brand.name}`}
                  aria-current={brand._id === selectedBrandId ? 'page' : undefined}
                >
                  <div className="font-medium text-gray-900">{brand.name}</div>
                  <div className="text-xs text-gray-600">Work on this brand</div>
                  {brand._id === selectedBrandId && (
                    <div className="text-[11px] text-blue-700 font-semibold mt-1">Selected</div>
                  )}
                </button>
              ))}

              {!brands?.length && (
                <div className="text-sm text-gray-600">No brands found in Sanity.</div>
              )}
            </div>
          )}

          <div className="mt-4">
            <Link
              to={storedBrandId ? `/?brandId=${encodeURIComponent(storedBrandId)}` : '/'}
              className="inline-block text-blue-600 underline text-sm"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}

